import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface ButtonProps {
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
}

interface ButtonGroupProps {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
}

interface ToolbarProps {
  children: React.ReactNode;
  className?: string;
}

// 按钮组件
export const Button: React.FC<ButtonProps> = ({
  onClick,
  disabled = false,
  variant = 'secondary',
  size = 'md',
  children,
  className = ''
}) => {
  const { colors } = useTheme();
  
  // 基础样式
  const baseClasses = 'font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  
  // 尺寸样式
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base'
  };
  
  // 获取变体样式
  const getVariantStyle = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: colors.accent,
          color: 'white',
          borderColor: colors.accent,
        };
      case 'secondary':
        return {
          backgroundColor: colors.surface,
          color: colors.text,
          borderColor: colors.border,
        };
      case 'danger':
        return {
          backgroundColor: colors.danger,
          color: 'white',
          borderColor: colors.danger,
        };
      case 'success':
        return {
          backgroundColor: colors.success,
          color: 'white',
          borderColor: colors.success,
        };
      case 'warning':
        return {
          backgroundColor: colors.warning,
          color: 'white',
          borderColor: colors.warning,
        };
      default:
        return {
          backgroundColor: colors.surface,
          color: colors.text,
          borderColor: colors.border,
        };
    }
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${sizeClasses[size]} border ${className}`}
      style={getVariantStyle()}
    >
      {children}
    </button>
  );
};

// 按钮组组件
export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  align = 'left',
  gap = 'md',
  className = ''
}) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end'
  };
  
  const gapClasses = {
    sm: 'gap-1',
    md: 'gap-2',
    lg: 'gap-3'
  };
  
  return (
    <div className={`flex flex-wrap items-center ${alignClasses[align]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

// 分隔线组件
export const ButtonDivider: React.FC = () => {
  const { colors } = useTheme();
  return (
    <div 
      className="h-8 w-px mx-2" 
      style={{ backgroundColor: colors.borderLight }}
    />
  );
};

// 工具栏组件
export const Toolbar: React.FC<ToolbarProps> = ({ children, className = '' }) => {
  const { colors } = useTheme();
  return (
    <div 
      className={`border rounded-lg p-3 mb-4 ${className}`}
      style={{
        backgroundColor: colors.surface,
        borderColor: colors.borderLight,
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        {children}
      </div>
    </div>
  );
};

// 工具栏组标题
export const ToolbarGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const { colors } = useTheme();
  return (
    <div className="flex flex-col gap-1">
      <span 
        className="text-xs font-medium uppercase tracking-wider"
        style={{ color: colors.textMuted }}
      >
        {title}
      </span>
      <ButtonGroup gap="sm">
        {children}
      </ButtonGroup>
    </div>
  );
}; 